// config/routes.ts - Route configuration
import ActivityDetails from "../pages/activity/ActivityDetails";
import Notifications from "../pages/notifications/Notifications";
import LoginPage from "../components/auth/Login";
import OAuthCallback from "../pages/auth/OAuthCallback";
import VerifyPage from "../components/auth/VerifyLogin";
import SettingsPage from "../pages/settings/Settings";
import RemotePage from "../pages/remote/Remote";
import ScanList from "../pages/remote/subcompoenents/ScanList";
import BiWazeDeviceDetail from "../pages/remote/subcompoenents/DeviceInfo";
import TherapyInsights from "../pages/remote/subcompoenents/TherapyInsights";
import HomePageGuest from "../components/layout/Guest/HomePage";
import MyAccountPage from "../pages/settings/MyAccount";
import MyDevicesPage from "../pages/settings/MyDevices";
import MyGoals from "../pages/settings/MyGoals";
import NotificationPreferences from "../pages/settings/NotificationPreferences";
import MyPending from "../pages/settings/PendingRequests";
import MyHealthConnect from "../pages/settings/MyHealthData";
import MyReflections from "../pages/Journal/MyReflections";
import MyNotifications from "../pages/settings/MyNotifications";
import MyProfilePage from "../pages/settings/MyProfile";
import ChangeProfilePic from "../pages/settings/ChangeProfilePic";
import MyBiometricSettings from "../pages/settings/MyBioMetric";
import SendReport from "../pages/activity/SendReport";
import MyFollowers from "../pages/settings/followers/MyFollowers";
import MyFollowing from "../pages/settings/following/MyFollowing";
import CurvesScreen from "../pages/remote/subcompoenents/RealTimeCurves";
import PrivacyPolicy from "../pages/settings/help/PrivacyPolicy";
import TermsAndConditions from "../pages/settings/help/Terms";
import HelpFaq from "../pages/settings/help/HelpFaq";
import ContactUs from "../pages/settings/help/ContactUs";
import HealthPluginTester from "../pages/settings/debug/HealthPluginTester";
// Patient engagement (E12c) — these talk to the KERNEL, not blaze. See
// src/services/engagement.ts and .arc-board/PATIENT-TOUCHPOINTS.md.
import CareTeamMessages from "../pages/engagement/CareTeamMessages";
import CareTeamThread from "../pages/engagement/CareTeamThread";
import CheckIn from "../pages/engagement/CheckIn";
import CareTeamSurveys from "../pages/engagement/CareTeamSurveys";
import {
  HomeIcon,
  JournalIcon,
  RemoteButtonIcon,
  UserIcon,
} from "../assets/icons/index";
import HomeDashboard from "../pages/HomeDashboard";
import PulseOximeterDetail from "../pages/remote/subcompoenents/PulseOximeterDeviceInfo";
import React from "react";

export const ROUTES_WITHOUT_NAV = [
  "/signin",
  "/ifu",
  "/signup",
  "/verify",
  "/activity/send-report",
  "/settings/account",
  "/settings/devices",
  "/settings/edit-profile",
  "/settings/edit-goals",
  "/settings/notifications",
  "/settings/notifications/preferences",
  "/settings/health-connect",
  "/settings/change-profile-pic",
  "/settings/my-health-access",
  "/remote/scan",
  "/remote/deviceInfo",
  "/remote/pulseOximeterInfo",
  "/remote/analytics",
  "/settings/notification/preferences",
  "/settings/followers",
  "/settings/pending-requests",
  "/settings/following",
  "/remote/curves",
  "/settings/biometric-settings",
  "/settings/privacy-policy",
  "/settings/terms-of-use",
  "/settings/contact-us",
  "/settings/help",
  "/remote",
  "/activity/details/cough",
  "/activity/details/clear",
  "/settings/notification/preferences/:userId",
  "/settings/debug/health-plugin-tester",
  // ⚠️ `/messages/:threadId` is NOT listed: `ROUTES_WITHOUT_NAV` is matched against the
  // literal pathname, so a parameterised entry would never match. The thread screen keeps
  // the bottom nav, which is the same choice `/activity/details/:id` makes.
  "/messages",
  "/check-in",
  "/surveys"
];

export interface RouteConfig {
  path: string;
  name: string;
  component?: React.ComponentType<any>;
  requiresAuth: boolean;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  showInTabs: boolean;
  hideNav?: boolean;
  invert?: boolean;
  redirectTo?: string;
}

export const routes: RouteConfig[] = [
  {
    path: "/home",
    name: "home",
    component: HomeDashboard,
    requiresAuth: true,
    icon: HomeIcon,
    showInTabs: true,
  },
  {
    path: "/activity/details/:id",
    name: "activity-details",
    component: ActivityDetails,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/notifications",
    name: "notifications",
    component: Notifications,
    requiresAuth: true,
    showInTabs: false,
  },
  // ── Patient engagement — Epic 12 patient side (E12c) ───────────────────────────────────
  // ⛔ NOT IN THE TAB BAR. The bar has four slots and they are the product's spine (home,
  // reflections, remote, settings); engagement is reached from Settings and from the
  // notifications screen, the same way every other secondary surface is. Adding a fifth tab
  // is a product decision, not a wiring one.
  {
    path: "/messages/:threadId",
    name: "care-team-thread",
    component: CareTeamThread,
    requiresAuth: true,
    showInTabs: false,
  },
  {
    path: "/messages",
    name: "care-team-messages",
    component: CareTeamMessages,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/check-in",
    name: "daily-check-in",
    component: CheckIn,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  // F-486 — Settings > CARE TEAM > Surveys, the 30/60/90-day surveys' own category.
  {
    path: "/surveys",
    name: "care-team-surveys",
    component: CareTeamSurveys,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/home-guest",
    name: "home-guest",
    component: HomePageGuest,
    requiresAuth: false,
    icon: HomeIcon,
    showInTabs: false,
  },
  {
    path: "/journal",
    name: "reflections",
    component: MyReflections,
    requiresAuth: true,
    icon: JournalIcon,
    showInTabs: true,
  },
  {
    path: "/settings",
    name: "settings",
    component: SettingsPage,
    requiresAuth: true,
    icon: UserIcon,
    showInTabs: true,
  },
  {
    path: "/remote",
    name: "remote",
    component: RemotePage,
    requiresAuth: false,
    icon: RemoteButtonIcon,
    showInTabs: true,
    hideNav: true,
    invert: false,
  },
  {
    path: "/remote/scan",
    name: "remote",
    component: ScanList,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/remote/deviceInfo",
    name: "remote",
    component: BiWazeDeviceDetail,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/remote/pulseOximeterInfo",
    name: "remote",
    component: PulseOximeterDetail,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/remote/analytics",
    name: "remote",
    component: TherapyInsights,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/remote/curves",
    name: "remote",
    component: CurvesScreen,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/account",
    name: "Account",
    component: MyAccountPage,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/devices",
    name: "My Devices",
    component: MyDevicesPage,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },

  {
    path: "/settings/notification/preferences/:userId?",
    name: "Notification Preferences",
    component: NotificationPreferences,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/notifications",
    name: "My Devices",
    component: MyNotifications,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/edit-profile/:imageDataUrl?",
    name: "My Profile",
    component: MyProfilePage,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/edit-goals",
    name: "My Profile",
    component: MyGoals,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/followers",
    name: "My Followers",
    component: MyFollowers,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/pending-requests/:pageType",
    name: "Data Sharing",
    component: MyPending,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/following",
    name: "Data Sharing",
    component: MyFollowing,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/health-connect",
    name: "My Followers",
    component: MyHealthConnect,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/biometric-settings",
    name: "My Biometric Data",
    component: MyBiometricSettings,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/change-profile-pic",
    name: "Change Profile Pic",
    component: ChangeProfilePic,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/privacy-policy",
    name: "Privacy Policy",
    component: PrivacyPolicy,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/terms-of-use",
    name: "Terms of Use",
    component: TermsAndConditions,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/help",
    name: "Help & FAQ",
    component: HelpFaq,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/contact-us",
    name: "Contact Us",
    component: ContactUs,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/settings/debug/health-plugin-tester",
    name: "Health Plugin Tester",
    component: HealthPluginTester,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/verify",
    name: "Verify",
    component: VerifyPage,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/activity/send-report",
    name: "Send Report",
    component: SendReport,
    requiresAuth: true,
    showInTabs: false,
    hideNav: true,
  },
  {
    // WEB-only: the Cognito Hosted-UI social redirect lands here. Native uses the
    // arcconnect:// deep link instead (D36). Without this entry the redirect fell through
    // to AppRoutes' `path="*"` catch-all and was silently redirected to /home, dropping the
    // authorization code — web social sign-in could never complete.
    path: "/oauth/callback",
    name: "OAuth Callback",
    component: OAuthCallback,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/signin",
    name: "Sign In",
    component: LoginPage,
    requiresAuth: false,
    showInTabs: false,
    hideNav: true,
  },
  {
    path: "/",
    name: "Root",
    redirectTo: "/home",
    requiresAuth: false,
    showInTabs: false,
  },
];
// Show only routes that should appear in the bottom navigation tabs
export const tabRoutes = routes.filter((route) => route.showInTabs);
// Routes where the bottom nav should be hidden
export const noNavRoutes = routes.filter((route) => route.hideNav);
